import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { revenueApi, type PaginatedRoyaltyStatementsResponse } from "@/lib/api";
import type { RoyaltyStatement, RoyaltyStatementStatus } from "@/types";
import { DEFAULT_PAGE_SIZE } from "@/lib/constants";
import { useOrganizationStore } from "@/stores/organizationStore";
import { POLLING_INTERVAL } from "@/hooks/useInterval";
import { isRoyaltyStatementGenerating } from "@/lib/royalty";
export type { RoyaltyStatementStatus };

interface UseRoyaltyStatementsOptions {
  enabled?: boolean;
  search?: string;
  pageSize?: number;
  page?: number;
  status?: RoyaltyStatementStatus[];
  payees?: string[];
  contract?: string;
  currency?: string;
  ordering?: string;
  periodStartAfter?: string;
  periodEndBefore?: string;
}

interface UseRoyaltyStatementsReturn {
  statements: RoyaltyStatement[];
  count: number;
  next: string | null;
  previous: string | null;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: Error | null;
  isSuccess: boolean;
  refetch: () => void;
}

const createRoyaltyStatementsQueryKey = (
  labelId: string | undefined,
  search: string | undefined,
  pageSize: number | undefined,
  page: number | undefined,
  status: RoyaltyStatementStatus[] | undefined,
  payees: string[] | undefined,
  contract: string | undefined,
  currency: string | undefined,
  ordering: string | undefined,
  periodStartAfter: string | undefined,
  periodEndBefore: string | undefined,
) => {
  const key: (string | number)[] = ["royalty-statements"];

  if (labelId) key.push("label", labelId);

  const normalizedSearch = search?.trim() || null;
  if (normalizedSearch) key.push("search", normalizedSearch);

  if (pageSize && pageSize !== DEFAULT_PAGE_SIZE) key.push("pageSize", pageSize);
  if (page && page > 1) key.push("page", page);

  if (status && status.length > 0) key.push("status", status.sort().join(","));
  if (payees && payees.length > 0) key.push("payees", payees.sort().join(","));
  if (contract) key.push("contract", contract);
  if (currency) key.push("currency", currency);
  if (ordering) key.push("ordering", ordering);
  if (periodStartAfter) key.push("periodStartAfter", periodStartAfter);
  if (periodEndBefore) key.push("periodEndBefore", periodEndBefore);

  return key;
};

export const useRoyaltyStatements = ({
  enabled = true,
  search,
  pageSize,
  page,
  status,
  payees,
  contract,
  currency,
  ordering,
  periodStartAfter,
  periodEndBefore,
}: UseRoyaltyStatementsOptions = {}): UseRoyaltyStatementsReturn => {
  const { currentLabel } = useOrganizationStore();

  const params = {
    label_id: currentLabel?.id,
    ...(search && { q: search }),
    ...(pageSize && { page_size: pageSize }),
    ...(page && { page }),
    ...(status && status.length > 0 && { status: status.join(",") }),
    ...(payees && payees.length > 0 && { payees: payees.join(",") }),
    ...(contract && { contract }),
    ...(currency && { currency }),
    ...(ordering && { ordering }),
    ...(periodStartAfter && { period_start_after: periodStartAfter }),
    ...(periodEndBefore && { period_end_before: periodEndBefore }),
  };

  const queryKey = createRoyaltyStatementsQueryKey(
    currentLabel?.id,
    search,
    pageSize,
    page,
    status,
    payees,
    contract,
    currency,
    ordering,
    periodStartAfter,
    periodEndBefore,
  );

  const { data, isLoading, isFetching, isError, error, isSuccess, refetch } = useQuery({
    queryKey,
    queryFn: async (): Promise<PaginatedRoyaltyStatementsResponse> => {
      return await revenueApi.getRoyaltyStatements(params);
    },
    enabled,
    placeholderData: keepPreviousData,
    // Statements are created as `pending` and filled in (PDF + money fields)
    // by a background task, so keep polling until every visible statement
    // reaches a terminal status — and its revenue CSV has been prepared.
    refetchInterval: (query) =>
      query.state.data?.results.some(
        (s) => isRoyaltyStatementGenerating(s.status) || s.revenue_csv_status === "preparing",
      )
        ? POLLING_INTERVAL
        : false,
  });

  return {
    statements: data?.results ?? [],
    count: data?.count ?? 0,
    next: data?.next ?? null,
    previous: data?.previous ?? null,
    isLoading,
    isFetching,
    isError,
    error,
    isSuccess,
    refetch,
  };
};
